import { InfoOutlined } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import useStringTranslation from "../../../../../../hooks/useStringTranslation";
import useUser from "../../../../../../hooks/useUser";
import DialogCore from "../../../../components/core/DialogCore";
import { BoxBorder } from "../../../../components/forms/AddBox";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";

const InfoItem = ({ label, value }) => (
  <Typography>
    <strong>{label}:</strong> {value || "—"}
  </Typography>
);

// Actions-dialog row that opens the pet details dialog
export default function PetDetails({ pet }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <ActionRow
        icon={<InfoOutlined />}
        label={t("ShowDetails")}
        description={t("ShowPetDetailsDescription")}
        onClick={() => setOpen(true)}
        testId="pet.header.actions.details"
      />
      <DialogCore
        open={open}
        onClose={() => setOpen(false)}
        showSubmit={false}
        fontWeight={800}
        maxWidth="lg"
        title={
          pet == null
            ? t("PetDetailsTitle")
            : `${pet.name} (${t("PetDetailsTitle")})`
        }
      >
        {pet == null ? (
          <Typography color={"GrayText"}>{t("PetNotFound")}</Typography>
        ) : (
          <Stack spacing={3}>
            <BasicInformation pet={pet} />
            <Identification pet={pet} />
            <BloodDonation pet={pet} />
            {/* The owner already knows who owns the pet, and aggression is clinic-facing */}
            {!roles.user && <Aggression pet={pet} />}
            {!roles.user && <Ownership pet={pet} />}
          </Stack>
        )}
      </DialogCore>
    </>
  );
}

function Identification({ pet = {} }) {
  const { t } = useTranslation();
  if (!pet?.microchip && !pet?.passport) {
    return (
      <Section title={t("Identification")}>
        <Typography color={"GrayText"}>{t("NoIdentificationData")}</Typography>
      </Section>
    );
  }
  return (
    <Section title={t("Identification")}>
      <InfoItem label={t("Microchip")} value={pet?.microchip} />
      <InfoItem label={t("PassportID")} value={pet?.passport} />
    </Section>
  );
}

function BloodDonation({ pet = {} }) {
  const { t } = useTranslation();
  return (
    <Section title={t("BloodDonor")}>
      <InfoItem label={t("BloodDonor")} value={pet?.bloodDonor ? t("Yes") : t("No")} />
      {pet?.bloodDonor && (
        <InfoItem label={t("BloodType")} value={pet?.bloodType} />
      )}
    </Section>
  );
}

function Aggression({ pet = {} }) {
  const { t } = useTranslation();
  if (!pet?.aggression) return null;
  return (
    <Section title={t("Aggression")}>
      <Typography sx={{ whiteSpace: "pre-wrap" }}>{pet.aggression}</Typography>
    </Section>
  );
}

function BasicInformation({ pet = {} }) {
  const { t } = useTranslation();
  const species = useStringTranslation(pet?.type, "title");

  return (
    <Section title={t("BasicInformation")}>
      <InfoItem label={t("Species")} value={species} />
      <InfoItem label={t("Breed")} value={t(pet?.breed)} />
      <InfoItem
        label={t("BirthDate")}
        value={pet?.birthdate || ""}
      />
      <InfoItem label={t("Gender")} value={t(pet?.gender)} />
    </Section>
  );
}

function Ownership({ pet = {} }) {
  const { t } = useTranslation();
  if (pet?.userId) {
    return (
      <Section title={t("Owner")}>
        <InfoItem label={t("Name")} value={pet?.ownerName} />
        <InfoItem label={t("Email")} value={pet?.ownerEmail} />
        <InfoItem
          label={t("PhoneNumber")}
          value={pet?.ownerPhoneNumber}
        />
      </Section>
    );
  }
  if (pet?.clinicId) {
    return (
      <Section title={t("Clinic")}>
        <InfoItem label={t("Name")} value={pet?.clinicShortName} />
        <InfoItem label={t("Email")} value={pet?.clinicEmail} />
        <InfoItem label={t("PhoneNumber")} value={pet?.clinicPhoneNumber} />
      </Section>
    );
  }
  return (
    <Section title={t("Owner")}>
      <Typography color={"GrayText"}>{t("NoOwnershipData")}</Typography>
    </Section>
  );
}

const Section = ({ title, children }) => (
  <BoxBorder>
    <Typography variant="h6" fontWeight={"bold"} gutterBottom>
      {title}
    </Typography>
    <Stack spacing={1}>{children}</Stack>
  </BoxBorder>
);
